import { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";

import LoaderComponenet from "../components/common/Loader";

import { PrivateRoute } from "./PrivateRoute";
import {
  consumeTokenFromHash,
  fetchAccessContext,
  getEnvBearerToken,
  GINJA_COOKIE_NAME,
  GINJA_LOCAL_STORAGE_KEYS,
  isAuthenticatedSession,
  mapGinjaAccessContextToAppUser,
  mapStoredGinjaUserInfoToAppUser,
  persistAccessContext,
  readCookie,
  SSO_COOKIE_SENTINEL,
} from "../services/ginjaAuth";
import {
  applyBrandSettings,
  applyCachedBrandSettings,
  fetchBrandSettings,
  resetBrandToDefaults,
} from "../services/ginjaBrand";

const ProvidersOnboarding = lazy(
  () => import("../components/Onboarding/Providers"),
);
const AssignedProviders = lazy(
  () => import("../components/Onboarding/Providers/AssignedProviders"),
);
const AuditProvider = lazy(
  () => import("../components/Onboarding/Providers/AssignedProviders/Audit"),
);
const ProviderDetails = lazy(
  () => import("../components/Onboarding/Providers/Details"),
);

const Login = lazy(() => import("../components/Auth/Login"));
const Welcome = lazy(() => import("../components/Auth/Welcome"));
const RequestToken = lazy(
  () => import("../components/Auth/ResetPassword/RequestToken"),
);
const ResetPassword = lazy(() => import("../components/Auth/ResetPassword"));
const Users = lazy(() => import("../components/Auth/Users"));

const NotFound = lazy(() => import("../components/common/404"));

const RouteSetup = () => {
  const Onboarding =
    JSON.parse(localStorage.getItem("user"))?.team_role?.[0]?.role ===
    "Relationship_Manager" ? (
      <>
        <Route
          path="/"
          element={
            <PrivateRoute>
              <AssignedProviders />
            </PrivateRoute>
          }
        />
        <Route
          path="/providers"
          element={
            <PrivateRoute>
              <ProvidersOnboarding />
            </PrivateRoute>
          }
        />
        <Route
          path="/assigned-providers"
          element={
            <PrivateRoute>
              <AssignedProviders />
            </PrivateRoute>
          }
        />
        <Route
          path="/assigned-providers/:account_id/audit/"
          element={
            <PrivateRoute>
              <AuditProvider />
            </PrivateRoute>
          }
        />
      </>
    ) : (
      <>
        <Route
          path="/"
          element={
            <PrivateRoute>
              <ProvidersOnboarding />
            </PrivateRoute>
          }
        />
        <Route
          path="/providers"
          element={
            <PrivateRoute>
              <ProvidersOnboarding />
            </PrivateRoute>
          }
        />
        <Route
          path="/providers/:account_id/details"
          element={
            <PrivateRoute>
              <ProviderDetails />
            </PrivateRoute>
          }
        />

        <Route
          path="/assigned-providers"
          element={
            <PrivateRoute>
              <AssignedProviders />
            </PrivateRoute>
          }
        />
        <Route
          path="/assigned-providers/:account_id/audit/"
          element={
            <PrivateRoute>
              <AuditProvider />
            </PrivateRoute>
          }
        />
      </>
    );

  const [loading, setLoading] = useState(true);
  const user = localStorage.getItem("user");
  const navigate = useNavigate();
  useEffect(() => {
    let cancelled = false;

    const runBootstrap = async () => {
      const isAuthPage =
        window.location.pathname === "/welcome" ||
        window.location.pathname === "/reset-password" ||
        window.location.pathname.startsWith("/reset-password/");

      // Allow legacy welcome/reset pages to load without auth.
      if (isAuthPage) {
        if (!cancelled) setLoading(false);
        return;
      }

      try {
        // 1) Hash token handoff from parent.
        const tokenFromHash = consumeTokenFromHash();

        // 2) Optional readable cookie (HttpOnly cookies still flow via credentials: "include").
        const tokenFromCookie = readCookie(GINJA_COOKIE_NAME);

        // 3) Validate via /me/access with best available bearer hint.
        const envBearer = getEnvBearerToken();
        const cachedAuthToken = localStorage.getItem(
          GINJA_LOCAL_STORAGE_KEYS.authToken,
        );
        const bearerHint =
          tokenFromHash ||
          tokenFromCookie ||
          (cachedAuthToken && cachedAuthToken !== SSO_COOKIE_SENTINEL
            ? cachedAuthToken
            : null) ||
          envBearer ||
          null;

        let accessContext = null;
        try {
          accessContext = await fetchAccessContext(bearerHint);
        } catch {
          accessContext = null;
        }

        if (accessContext) {
          const authTokenToPersist =
            tokenFromHash ||
            tokenFromCookie ||
            bearerHint ||
            envBearer ||
            SSO_COOKIE_SENTINEL;
          persistAccessContext(authTokenToPersist, accessContext);

          const appUser = mapGinjaAccessContextToAppUser(accessContext);
          localStorage.setItem(
            GINJA_LOCAL_STORAGE_KEYS.user,
            JSON.stringify(appUser),
          );

          // Product API bearer is only set when we have a real JWT (not sentinel).
          if (
            authTokenToPersist &&
            authTokenToPersist !== SSO_COOKIE_SENTINEL
          ) {
            localStorage.setItem(
              GINJA_LOCAL_STORAGE_KEYS.token,
              authTokenToPersist,
            );
          } else {
            localStorage.removeItem(GINJA_LOCAL_STORAGE_KEYS.token);
          }

          // Brand settings best-effort.
          try {
            const brandRes = await fetchBrandSettings();
            if (brandRes?.data) applyBrandSettings(brandRes.data);
          } catch {
            applyCachedBrandSettings();
          }

          // If user landed on /login and SSO succeeded, navigate home.
          if (window.location.pathname === "/login") {
            navigate("/", { replace: true });
          }
        } else {
          // 5) Restore from cached Ginja userInfo if present.
          const cachedAuthToken = localStorage.getItem(
            GINJA_LOCAL_STORAGE_KEYS.authToken,
          );
          if (cachedAuthToken) {
            const restored = mapStoredGinjaUserInfoToAppUser();
            if (restored)
              localStorage.setItem(
                GINJA_LOCAL_STORAGE_KEYS.user,
                JSON.stringify(restored),
              );

            try {
              const brandRes = await fetchBrandSettings();
              if (brandRes?.data) applyBrandSettings(brandRes.data);
            } catch {
              applyCachedBrandSettings();
            }
          } else {
            // No cached SSO; leave legacy token/user as-is.
            applyCachedBrandSettings();
          }
        }
      } finally {
        if (!cancelled) {
          // Temporary fallback: if no auth session exists, default to an Operations Manager view.
          // This prevents landing on the legacy login screen while you wire up role-based rendering.
          if (!isAuthenticatedSession()) {
            const fallbackUser = {
              name: "Operations Manager",
              team_role: [{ role: "Operations_Manager" }],
            };
            localStorage.setItem(
              GINJA_LOCAL_STORAGE_KEYS.user,
              JSON.stringify(fallbackUser),
            );
            localStorage.setItem(
              GINJA_LOCAL_STORAGE_KEYS.authToken,
              SSO_COOKIE_SENTINEL,
            );

            // If currently on /login, take the user home.
            if (window.location.pathname === "/login") {
              navigate("/", { replace: true });
            }
          }
          setLoading(false);
        }
      }
    };

    runBootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  return loading ? (
    <LoaderComponenet />
  ) : (
    <Suspense fallback={<LoaderComponenet />}>
      <Routes>
        {/* {Finance} */}
        {Onboarding}
        {/* {JSON.parse(user)?.team_role?.filter(tr => tr.team === 'Claim').length > 0 && ClaimProcessing}
        {JSON.parse(user)?.team_role?.filter(tr => tr.team === 'Finance').length > 0 && Finance}
        {(JSON.parse(user)?.team_role?.filter(tr => tr.team === 'Pre_Authorization').length > 0 || JSON.parse(user)?.team_role?.filter(tr => tr.team === 'Claim').length > 0) && PreAuthProcessing}
        {(JSON.parse(user)?.team_role?.filter(tr => tr.team === 'Pre_Authorization').length > 0 || JSON.parse(user)?.team_role?.filter(tr => tr.team === 'Claim').length > 0) && Notifcations}
        {(JSON.parse(user)?.team_role?.filter(tr => tr.team === 'Support').length > 0 || JSON.parse(user)?.team_role?.filter(tr => tr.team === 'Pre_Authorization').length > 0) && CustomerSupport}
        {Underwriting} */}

        {/* {Notifcations} */}
        {/* {Reports} */}
        {/* {(JSON.parse(user)?.team_role?.filter(tr => tr.team === 'Support').length > 0 || JSON.parse(user)?.team_role?.filter(tr => tr.team === 'Claim').length > 0) && Reports} */}
        {/* {(JSON.parse(user)?.team_role?.filter(tr => tr.team === 'Support').length > 0 || JSON.parse(user)?.team_role?.filter(tr => tr.team === 'Claim').length > 0) && Insights} */}
        {/* {Sales} */}
        <Route
          path="/users"
          element={
            <PrivateRoute>
              <Users />
            </PrivateRoute>
          }
        />
        <Route path="/login" element={<Login />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/reset-password" element={<RequestToken />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route
          path="*"
          element={
            <PrivateRoute>
              <NotFound />
            </PrivateRoute>
          }
        />
      </Routes>
    </Suspense>
  );
};

export default RouteSetup;
